import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useNotification } from '../../context/NotificationContext';
import { History, Bot, User, Undo2 } from 'lucide-react';
import './AuditLogTable.css';

const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
};

function AuditLogTable() {
    const { showError, showSuccess } = useNotification();
    const [logs, setLogs] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/audit', { params: { page, limit: 20 } });
            setLogs(Array.isArray(data) ? data : data.logs || []);
        } catch (err) {
            console.error("Failed to load audit logs", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [page]);

    const handleRollback = async (logId) => {
        if (!window.confirm('Are you sure you want to undo this action? This will restore the previous data state.')) return;
        try {
            await api.post(`/audit/${logId}/rollback`);
            showSuccess('Action undone successfully.');
            fetchLogs(); // Reload after rollback
        } catch (err) {
            console.error("Failed to rollback action", err);
            showError(err.response?.data?.detail || "Failed to undo action.");
        }
    };

    return (
        <div className="card audit-log-container">
            <div className="audit-log-header">
                <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>
                    <History size={18} /> Audit Log
                </h2>
                <div className="pagination">
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</button>
                    <span>Page {page}</span>
                    <button onClick={() => setPage(p => p + 1)} disabled={logs.length < 20}>Next</button>
                </div>
            </div>

            {loading ? (
                <div className="loading-state">Loading audit history...</div>
            ) : logs.length === 0 ? (
                <div className="empty-state">No audit logs found.</div>
            ) : (
                <div className="table-responsive">
                    <table className="audit-table">
                        <thead>
                            <tr>
                                <th>Time</th>
                                <th>Action</th>
                                <th>Changes</th>
                                <th>Source</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map(log => (
                                <tr key={log.id || log._id} className={log.rolled_back ? 'rolled-back-row' : ''}>
                                    <td className="time-cell">{formatDate(log.executed_at)}</td>
                                    <td>
                                        <span className="action-badge">{log.action_type}</span>
                                    </td>
                                    <td className="changes-cell">
                                        <div className="change-details">
                                            <div className="change-before">
                                                <span className="change-label">Was:</span>
                                                <code>{JSON.stringify(log.before || {})}</code>
                                            </div>
                                            <div className="change-after">
                                                <span className="change-label">Now:</span>
                                                <code>{JSON.stringify(log.after || {})}</code>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="source-badge">{log.triggered_by || 'system'}</span>
                                    </td>
                                    <td>
                                        {log.rolled_back ? (
                                            <span className="status-badge warning">Undone</span>
                                        ) : (
                                            <span className="status-badge success">Executed</span>
                                        )}
                                    </td>
                                    <td>
                                        {!log.rolled_back && Object.keys(log.before || {}).length > 0 && (
                                            <button className="btn" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px', fontSize: '0.85rem' }} onClick={() => handleRollback(log.id || log._id)}>
                                                <Undo2 size={14} /> Undo
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default AuditLogTable;
